import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

export const runsLabel = (c) => {
  if (c.starts_on && c.ends_on) return `${fmt(c.starts_on)} – ${fmt(c.ends_on)}`;
  if (c.starts_on) return `From ${fmt(c.starts_on)}`;
  if (c.ends_on) return `Until ${fmt(c.ends_on)}`;
  return "Always";
};

export const DISCOUNT_PLANS = [["1mo", "Monthly"], ["annual", "Annual"], ["family_annual", "Family Annual"]];

export const planSummary = (c) => {
  const pp = c.plan_percents_resolved || {};
  const vals = DISCOUNT_PLANS.map(([k]) => pp[k] ?? c.percent_off);
  if (vals.every((v) => v === vals[0] && v > 0)) return `${vals[0]}% · all plans`;
  return DISCOUNT_PLANS.map(([, l], i) => (vals[i] > 0 ? `${l} ${vals[i]}%` : null)).filter(Boolean).join(" · ");
};

export function PlanPercentInputs({ value, onChange, prefix = "discount-code-pct", short = false }) {
  return DISCOUNT_PLANS.map(([k, label]) => (
    <div key={k}>
      <label className="overline block whitespace-nowrap">{short ? label.replace(" Annual", "") : `${label} % off`}</label>
      <input type="number" min={1} max={100} value={value[k] || ""} placeholder="—"
             onChange={(e) => onChange({ ...value, [k]: e.target.value })}
             data-testid={`${prefix}-${k}`} className="input-rf mt-1" />
    </div>
  ));
}

export function CodePlansEditor({ c, onSaved }) {
  const [open, setOpen] = useState(false);
  const [pp, setPp] = useState(c.plan_percents_resolved || {});
  const save = async () => {
    try {
      await api.patch(`/admin/discount-codes/${c.code}`, { plan_percents: pp });
      toast.success("Plan discounts saved");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save plan discounts"); }
  };
  if (!open) {
    return (
      <button type="button" data-testid={`discount-code-edit-plans-${c.code}`} onClick={() => setOpen(true)} className="text-xs font-bold text-rf-orange hover:underline mr-3">
        Plans
      </button>
    );
  }
  return (
    <span className="flex flex-wrap items-end justify-end gap-2 mt-2 mb-2 text-left" data-testid={`discount-code-plans-form-${c.code}`}>
      <span className="grid grid-cols-3 gap-2 w-72"><PlanPercentInputs short value={pp} onChange={setPp} prefix={`discount-code-edit-pct-${c.code}`} /></span>
      <button type="button" onClick={save} data-testid={`discount-code-save-plans-${c.code}`} className="text-xs font-bold text-emerald-700 hover:underline">Save</button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-rf-ink-soft hover:underline">Cancel</button>
    </span>
  );
}

const STATUS = {
  disabled: ["Disabled", "text-red-600"], scheduled: ["Scheduled", "text-amber-700"],
  live: ["Live", "text-emerald-700"], ended: ["Ended", "text-rf-ink-soft"],
};

export const CodeStatus = ({ c }) => {
  const [label, cls] = STATUS[c.disabled ? "disabled" : c.window_status || "live"];
  return <span data-testid={`discount-code-status-${c.code}`} className={`text-[11px] font-bold uppercase tracking-wider ${cls}`}>{label}</span>;
};

export function CodeBannerEditor({ c, onSaved }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState(c.banner_message || "");
  const save = async (enabled) => {
    try {
      await api.patch(`/admin/discount-codes/${c.code}`, { banner_enabled: enabled, banner_message: msg });
      toast.success(enabled ? `${c.code} will show in the site banner while it's live` : "Removed from banner");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update banner"); }
  };
  if (!open) {
    return (
      <button type="button" data-testid={`discount-code-banner-${c.code}`} onClick={() => setOpen(true)}
              className={`text-xs font-bold hover:underline mr-3 ${c.banner_enabled ? "text-emerald-700" : "text-rf-orange"}`}>
        {c.banner_enabled ? "★ In banner" : "Banner"}
      </button>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1 mr-3" data-testid={`discount-code-banner-form-${c.code}`}>
      <input value={msg} onChange={(e) => setMsg(e.target.value)} maxLength={140} placeholder={planSummary(c)}
             data-testid={`discount-code-banner-message-${c.code}`} className="input-rf !py-1 !px-2 text-xs !w-56" />
      <button type="button" onClick={() => save(true)} data-testid={`discount-code-banner-save-${c.code}`} className="text-xs font-bold text-emerald-700 hover:underline">Show</button>
      {c.banner_enabled && <button type="button" onClick={() => save(false)} data-testid={`discount-code-banner-off-${c.code}`} className="text-xs font-bold text-red-600 hover:underline">Hide</button>}
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-rf-ink-soft hover:underline">Cancel</button>
    </span>
  );
}

export function CodeDatesEditor({ c, onSaved }) {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(c.starts_on || "");
  const [e, setE] = useState(c.ends_on || "");
  const save = async () => {
    try {
      await api.patch(`/admin/discount-codes/${c.code}`, { starts_on: s || null, ends_on: e || null });
      toast.success("Dates saved");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save dates"); }
  };
  if (!open) {
    return (
      <button type="button" data-testid={`discount-code-edit-dates-${c.code}`} onClick={() => setOpen(true)} className="text-xs font-bold text-rf-orange hover:underline mr-3">
        Dates
      </button>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1 mr-3" data-testid={`discount-code-dates-form-${c.code}`}>
      <input type="date" value={s} onChange={(ev) => setS(ev.target.value)} data-testid={`discount-code-edit-start-${c.code}`} className="input-rf !py-1 !px-2 text-xs !w-36" />
      <span className="text-xs">→</span>
      <input type="date" value={e} min={s || undefined} onChange={(ev) => setE(ev.target.value)} data-testid={`discount-code-edit-end-${c.code}`} className="input-rf !py-1 !px-2 text-xs !w-36" />
      <button type="button" onClick={save} data-testid={`discount-code-save-dates-${c.code}`} className="text-xs font-bold text-emerald-700 hover:underline">Save</button>
      <button type="button" onClick={() => setOpen(false)} className="text-xs text-rf-ink-soft hover:underline">Cancel</button>
    </span>
  );
}
